import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { usePaginatedVocabularyList } from "../../hooks/usePaginatedVocabularyList";
import {
  useMemo,
  useRef,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import useTranslation from "../../hooks/useTranslation";
import {
  computeVocabularyStage,
  formatRelativeToNow,
  streakStageKey,
} from "../../utils/vocabularyProgress";
import { TEXT_LANGUAGES } from "../../constants";
import {
  useMarkVocabularyAsForgotten,
  useMarkVocabularyAsLearned,
} from "../../hooks/useMarkVocabularyAsLearned";
import { useVocabularyStageCounts } from "../../hooks/useVocabularyStageCounts";
import {
  InformationCircleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { HandThumbUpIcon, HandThumbDownIcon } from "@heroicons/react/24/solid";
import { VocabularyDto } from "../../hooks/usePaginatedVocabularyList";
import { useVocabularyDelete } from "../../hooks/useVocabularyDelete";
import { notifySnackbar } from "../../lib/notifySnackbar";
import { useLanguageSpace } from "../../hooks/useLanguageSpace";
import { normalizeVocabularyTranslationDetails } from "../../utils/vocabularyTranslationDetails";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { highlightVocabularyWordInSentence } from "../../utils/highlightVocabularyWordInSentence";
import { VocabularyPronunciationButton } from "../../components/VocabularyPronunciationButton";

const VOCABULARY_REVIEWED_EVENT = "VOCABULARY_REVIEWED";

type VocabularyStageWithAll =
  | "ALL"
  | "NEW"
  | "LEARNING"
  | "REVIEWING"
  | "MASTERED"
  | "DUE";

type DerivedVocabulary = VocabularyDto & {
  stage: Exclude<VocabularyStageWithAll, "ALL">;
  nextReviewDate: Date | null;
  isDue: boolean;
};

const STAGE_META: {
  id: Exclude<VocabularyStageWithAll, "ALL">;
  labelKey: string;
  descriptionKey: string;
  accent: string;
  intervalKey?: string;
}[] = [
  {
    id: "DUE",
    labelKey: "vocabulary_progress_stage_due",
    descriptionKey: "vocabulary_progress_stage_due_description",
    accent: "bg-rose-50 text-rose-700 border-rose-200",
  },
  {
    id: "NEW",
    labelKey: "vocabulary_progress_stage_new",
    descriptionKey: "vocabulary_progress_stage_new_description",
    accent: "bg-blue-50 text-blue-700 border-blue-200",
  },
  {
    id: "LEARNING",
    labelKey: "vocabulary_progress_stage_learning",
    descriptionKey: "vocabulary_progress_stage_learning_description",
    accent: "bg-amber-50 text-amber-700 border-amber-200",
    intervalKey: "vocabulary_progress_stage_learning_interval",
  },
  {
    id: "REVIEWING",
    labelKey: "vocabulary_progress_stage_reviewing",
    descriptionKey: "vocabulary_progress_stage_reviewing_description",
    accent: "bg-indigo-50 text-indigo-700 border-indigo-200",
    intervalKey: "vocabulary_progress_stage_reviewing_interval",
  },
  {
    id: "MASTERED",
    labelKey: "vocabulary_progress_stage_mastered",
    descriptionKey: "vocabulary_progress_stage_mastered_description",
    accent: "bg-emerald-50 text-emerald-700 border-emerald-200",
    intervalKey: "vocabulary_progress_stage_mastered_interval",
  },
];

const SRS_SCHEDULE = [
  {
    streakLabel: "0",
    translationKey: "vocabulary_progress_srs_schedule_0",
  },
  {
    streakLabel: "1",
    translationKey: "vocabulary_progress_srs_schedule_1",
  },
  {
    streakLabel: "2",
    translationKey: "vocabulary_progress_srs_schedule_2",
  },
  {
    streakLabel: "3",
    translationKey: "vocabulary_progress_srs_schedule_3",
  },
  {
    streakLabel: "4",
    translationKey: "vocabulary_progress_srs_schedule_4",
  },
  {
    streakLabel: "5+",
    translationKey: "vocabulary_progress_srs_schedule_5",
  },
];

export const Route = createFileRoute("/app/vocabulary_progress")({
  validateSearch: (search: Record<string, unknown>) => ({
    tab: typeof search.tab === "string" ? search.tab : undefined,
  }),
  component: VocabularyProgressPage,
});

function VocabularyProgressPage() {
  const { translate } = useTranslation();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const { languageSpace } = useLanguageSpace("ENGLISH");
  const [languageFilters, setLanguageFilters] = useState<string[]>([]);
  const [stageFilter, setStageFilter] =
    useState<VocabularyStageWithAll>("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [showMoreGrammar, setShowMoreGrammar] = useLocalStorage<boolean>(
    "vocabulary-more-grammar-toggle-v2",
    false,
  );
  const [vocabularyToDelete, setVocabularyToDelete] =
    useState<DerivedVocabulary | null>(null);
  const initialOriginTab: "ALL" | "PHOTO" =
    tab === "photo" ? "PHOTO" : "ALL";
  const [originTab, setOriginTab] = useState<"ALL" | "PHOTO">(
    initialOriginTab,
  );
  const deleteVocabulary = useVocabularyDelete();
  const updateTabSearch = (value: "ALL" | "PHOTO") => {
        navigate({
          to: "/app/vocabulary_progress",
          search: { tab: value === "PHOTO" ? "photo" : undefined },
          replace: true,
        });
      };

  // Determine effective filters (space-locked vs manual multi-select)
  const effectiveLanguageFilters =
    languageSpace === "ALL" ? languageFilters : [languageSpace];

  const paginatedVocabularyList = usePaginatedVocabularyList({
    origins: originTab === "PHOTO" ? ["PHOTO_IMPORT"] : undefined,
    word_languages:
      effectiveLanguageFilters.length > 0
        ? effectiveLanguageFilters
        : undefined,
  });
  const stageCountsQuery = useVocabularyStageCounts({
    languages:
      effectiveLanguageFilters.length > 0
        ? effectiveLanguageFilters
        : undefined,
    origins: originTab === "PHOTO" ? ["PHOTO_IMPORT"] : undefined,
    maxStreak: 10000,
    textAgeInDays: 2000 * 365,
  });
  const { hasNextPage, isFetchingNextPage, fetchNextPage } =
    paginatedVocabularyList;
  const vocabularyList = useMemo(() => {
    if (!paginatedVocabularyList.isSuccess) {
      return [];
    }
    return paginatedVocabularyList.data.pages.flatMap((page) => page.results);
  }, [paginatedVocabularyList.data, paginatedVocabularyList.isSuccess]);

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }
    fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const nextTab: "ALL" | "PHOTO" = tab === "photo" ? "PHOTO" : "ALL";
    if (originTab !== nextTab) {
      setOriginTab(nextTab);
    }
  }, [originTab, tab]);

  useEffect(() => {
    const handler = () => {
      void paginatedVocabularyList.refetch();
      void stageCountsQuery.refetch();
    };
    document.addEventListener(VOCABULARY_REVIEWED_EVENT, handler);
    return () => {
      document.removeEventListener(VOCABULARY_REVIEWED_EVENT, handler);
    };
  }, [paginatedVocabularyList, stageCountsQuery]);

  const derivedVocabularyList: DerivedVocabulary[] = useMemo(() => {
    return vocabularyList.map((vocabulary) => {
      const { stage, nextReviewDate, isDue } = computeVocabularyStage({
        streak: vocabulary.winning_streak ?? 0,
        lastTested: vocabulary.last_tested,
      });
      return {
        ...vocabulary,
        stage,
        nextReviewDate,
        isDue,
      };
    });
  }, [vocabularyList]);

  // Sync language filters to the active language space. When the space is locked (not ALL),
  // force the filter to that language; when space is ALL, allow manual multi-select (default: all).
  useEffect(() => {
    if (languageSpace !== "ALL") {
      setLanguageFilters((prev) =>
        prev.length === 1 && prev[0] === languageSpace
          ? prev
          : [languageSpace],
      );
    } else {
      setLanguageFilters((prev) => (prev.length === 0 ? prev : []));
    }
  }, [languageSpace]);

  const filteredVocabulary = useMemo(() => {
    return derivedVocabularyList.filter((vocabulary) => {
      const matchesLanguage =
        effectiveLanguageFilters.length === 0 ||
        effectiveLanguageFilters.includes(vocabulary.word_language);
      const matchesStage =
        stageFilter === "ALL" || vocabulary.stage === stageFilter;
      const matchesSearch =
        searchTerm.trim().length === 0 ||
        vocabulary.word.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vocabulary.translation
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      return matchesLanguage && matchesStage && matchesSearch;
    });
  }, [
    derivedVocabularyList,
    effectiveLanguageFilters,
    stageFilter,
    searchTerm,
  ]);

  const sortedVocabulary = useMemo(() => {
    return [...filteredVocabulary].toSorted((a, b) => {
      if (a.isDue !== b.isDue) {
        return Number(b.isDue) - Number(a.isDue);
      }
      const aTime = a.nextReviewDate?.getTime() ?? Number.MAX_SAFE_INTEGER;
      const bTime = b.nextReviewDate?.getTime() ?? Number.MAX_SAFE_INTEGER;
      if (aTime !== bTime) {
        return aTime - bTime;
      }
      return a.word.localeCompare(b.word);
    });
  }, [filteredVocabulary]);

  const stageStats = useMemo(() => {
    const serverCounts = stageCountsQuery.data;
    if (serverCounts) {
      return serverCounts;
    }
    return filteredVocabulary.reduce<Record<string, number>>((acc, vocab) => {
      acc[vocab.stage] = (acc[vocab.stage] ?? 0) + 1;
      return acc;
    }, {});
  }, [filteredVocabulary, stageCountsQuery.data]);

  const languageSwitchOptions = useMemo(() => {
    const base = TEXT_LANGUAGES.map((lang) => ({
      value: lang.id,
      label: translate(lang.prettyTranslationKey),
    }));
    if (languageSpace !== "ALL") {
      return base.filter((lang) => lang.value === languageSpace);
    }
    return [
      {
        value: "ALL",
        label: translate("spaced_preview_language_all"),
      },
      ...base,
    ];
  }, [translate, languageSpace]);

  const handleDeleteRequest = (vocabulary: DerivedVocabulary) => {
    setVocabularyToDelete(vocabulary);
  };

  const handleCancelDelete = () => {
    if (deleteVocabulary.isPending) {
      return;
    }
    setVocabularyToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!vocabularyToDelete) {
      return;
    }
    try {
      await deleteVocabulary.mutateAsync({
        vocabularyId: vocabularyToDelete.id,
      });
      notifySnackbar({
        message: translate("vocabulary_progress_delete_success", {
          word: vocabularyToDelete.word,
        }),
      });
      setVocabularyToDelete(null);
    } catch (error) {
      console.error(error);
      notifySnackbar({
        message: translate("vocabulary_progress_delete_error"),
      });
    }
  };

  return (
    <div className="px-4 py-6 space-y-6">
      <header className="space-y-2">
        <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          {translate("vocabulary_progress_label")}
        </p>
        <h1 className="text-3xl font-bold text-slate-900">
          {translate("vocabulary_progress_title")}
        </h1>
        <p className="text-slate-600 max-w-3xl">
          {translate("vocabulary_progress_description")}
        </p>
      </header>
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => {
            setOriginTab("ALL");
            updateTabSearch("ALL");
          }}
          className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
            originTab === "ALL"
              ? "border-slate-900 bg-slate-900 text-white shadow-sm"
              : "border-slate-200 text-slate-600 hover:border-slate-400"
          }`}
        >
          {translate("vocabulary_tab_all")}
        </button>
        <button
          type="button"
          onClick={() => {
            setOriginTab("PHOTO");
            updateTabSearch("PHOTO");
          }}
          className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
            originTab === "PHOTO"
              ? "border-slate-900 bg-slate-900 text-white shadow-sm"
              : "border-slate-200 text-slate-600 hover:border-slate-400"
          }`}
        >
          {translate("vocabulary_tab_photo")}
        </button>
      </div>

      <section className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 md:mx-0 md:grid md:grid-cols-5 md:overflow-visible md:px-0 md:pb-0 md:snap-none">
        {STAGE_META.map((stage) => {
          const description = translate(stage.descriptionKey);
          const interval = stage.intervalKey
            ? translate(stage.intervalKey)
            : null;

          return (
            <button
              key={stage.id}
              type="button"
              onClick={() =>
                setStageFilter((current) =>
                  current === stage.id ? "ALL" : stage.id,
                )
              }
              className={`flex w-[min(85vw,16rem)] min-w-[11rem] flex-shrink-0 flex-col items-start rounded-2xl border p-3 text-left transition-all md:w-auto md:min-w-0 md:flex-shrink md:p-4 ${stage.accent} ${
                stageFilter === stage.id
                  ? "ring-2 ring-offset-2 ring-slate-900"
                  : ""
              }`}
            >
              <p className="text-sm font-semibold uppercase tracking-wide">
                {translate(stage.labelKey)}
              </p>
              <p className="text-2xl font-bold md:text-3xl">
                {stageStats[stage.id] ?? 0}
              </p>
              <p
                className="mt-2 text-xs text-slate-700 text-pretty leading-snug"
                title={description}
              >
                {description}
              </p>
              {interval && (
                <p
                  className="text-[11px] text-slate-600 text-pretty leading-snug"
                  title={interval}
                >
                  {interval}
                </p>
              )}
            </button>
          );
        })}
      </section>

      <SrsInfoPanel translate={translate} />

      <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-2 w-full lg:w-auto">
          <span className="text-xs font-semibold uppercase text-slate-500">
            {translate("vocabulary_progress_filters_language")}
          </span>
          <LanguageFilterDropdown
            selectedLanguages={languageFilters}
            onChange={setLanguageFilters}
            options={languageSwitchOptions}
            translate={translate}
            disabled={languageSpace !== "ALL"}
          />
        </div>
        <div className="flex flex-col gap-2 w-full lg:w-1/3">
          <span className="text-xs font-semibold uppercase text-slate-500">
            {translate("vocabulary_progress_filters_stage")}
          </span>
          <div className="flex flex-wrap gap-2">
            <FilterChip
              label={translate("vocabulary_progress_filters_stage_all")}
              active={stageFilter === "ALL"}
              onClick={() => setStageFilter("ALL")}
            />
            {STAGE_META.map((stage) => (
              <FilterChip
                key={stage.id}
                label={translate(stage.labelKey)}
                active={stageFilter === stage.id}
                onClick={() => setStageFilter(stage.id)}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2 w-full lg:w-1/3">
          <span className="text-xs font-semibold uppercase text-slate-500">
            {translate("vocabulary_progress_filters_search")}
          </span>
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={translate(
              "vocabulary_progress_filters_search_placeholder",
            )}
            className="w-full rounded-full border border-slate-200 px-4 py-2 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-100"
          />
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-semibold text-slate-900">
            {translate("vocabulary_progress_table_title")}
          </h2>
          <p className="text-sm text-slate-500">
            {translate("vocabulary_progress_table_showing")}{" "}
            {filteredVocabulary.length}
          </p>
        </div>

        {filteredVocabulary.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-slate-600">
            {translate("vocabulary_progress_empty_state")}
          </div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
              <table className="min-w-full divide-y divide-slate-100">
                <thead className="bg-slate-50">
                  <tr>
                    <TableHeader>{translate("vocabulary_progress_table_word")}</TableHeader>
                    <TableHeader>{translate("vocabulary_progress_table_translation")}</TableHeader>
                    <TableHeader>{translate("vocabulary_progress_table_stage")}</TableHeader>
                    <TableHeader>{translate("vocabulary_progress_table_streak")}</TableHeader>
                    <TableHeader>{translate("vocabulary_progress_table_last_practiced")}</TableHeader>
                    <TableHeader>{translate("vocabulary_progress_table_next_review")}</TableHeader>
                    <TableHeader>{translate("vocabulary_progress_table_actions")}</TableHeader>
                  </tr>
                </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                  {sortedVocabulary.map((vocabulary) => (
                    <VocabularyTableRow
                      key={vocabulary.id}
                      vocabulary={vocabulary}
                      translate={translate}
                      showMoreGrammar={showMoreGrammar}
                      setShowMoreGrammar={setShowMoreGrammar}
                      onDeleteRequest={handleDeleteRequest}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="space-y-3 md:hidden">
              {sortedVocabulary.map((vocabulary) => (
                <VocabularyCardRow
                  key={vocabulary.id}
                  vocabulary={vocabulary}
                  translate={translate}
                  showMoreGrammar={showMoreGrammar}
                  setShowMoreGrammar={setShowMoreGrammar}
                  onDeleteRequest={handleDeleteRequest}
                />
              ))}
            </div>
          </>
        )}
      </section>

      {isFetchingNextPage && (
        <p className="text-center text-sm text-slate-500">
          {translate("vocabulary_progress_loading_more")}
        </p>
      )}
      {vocabularyToDelete && (
        <DeleteVocabularyDialog
          vocabulary={vocabularyToDelete}
          translate={translate}
          isLoading={deleteVocabulary.isPending}
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  );
}

function VocabularyTableRow({
  vocabulary,
  translate,
  showMoreGrammar,
  setShowMoreGrammar,
  onDeleteRequest,
}: {
  vocabulary: DerivedVocabulary;
  translate: ReturnType<typeof useTranslation>["translate"];
  showMoreGrammar: boolean;
  setShowMoreGrammar: (value: boolean | ((prev: boolean) => boolean)) => void;
  onDeleteRequest: (vocabulary: DerivedVocabulary) => void;
}) {
  const markVocabularyAsLearned = useMarkVocabularyAsLearned();
  const markVocabularyAsForgotten = useMarkVocabularyAsForgotten();
  const translationDetails = useMemo(
    () =>
      normalizeVocabularyTranslationDetails({
        translation: vocabulary.translation,
        translation_details: vocabulary.translation_details,
      }),
    [vocabulary.translation, vocabulary.translation_details],
  );
  const sourceSentence = vocabulary.sentence?.trim() || null;
  const translatedSentence = translationDetails.translated_sentence;
  const primarySentence = sourceSentence;
  const secondarySentence = translatedSentence;

  const nextReviewCopy = vocabulary.nextReviewDate
    ? vocabulary.isDue
      ? translate("progress_due_now")
      : `${translate("progress_due_in")} ${formatRelativeToNow(vocabulary.nextReviewDate)}`
    : translate("progress_never");

  const lastPracticedCopy = vocabulary.last_tested
    ? new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      }).format(new Date(vocabulary.last_tested))
    : translate("progress_never");

  return (
    <tr className="hover:bg-slate-50">
      <TableCell>
        <div className="flex items-center gap-2">
          <p className="font-semibold">{vocabulary.word}</p>
          <VocabularyPronunciationButton vocabulary={vocabulary} />
        </div>
        <p className="text-xs text-slate-500">
          {vocabulary.word_language.replaceAll("_", " ")}
        </p>
      </TableCell>
      <TableCell>
        <p>{translationDetails.translation_word || vocabulary.translation}</p>
        <p className="mt-1 text-left text-xs text-slate-700">
          {primarySentence
            ? highlightVocabularyWordInSentence(primarySentence, vocabulary.word)
            : translate("vocabulary_source_sentence_unavailable")}
        </p>
        {secondarySentence && (
          <p className="mt-1 text-left text-xs text-slate-500">{secondarySentence}</p>
        )}
        <div className="mt-1 flex flex-wrap gap-1 text-[11px]">
          {translationDetails.grammar.part_of_speech && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
              {translate("vocabulary_label_pos")}:{" "}
              {translationDetails.grammar.part_of_speech}
            </span>
          )}
          {translationDetails.grammar.tense && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
              {translate("vocabulary_label_tense")}:{" "}
              {translationDetails.grammar.tense}
            </span>
          )}
          {translationDetails.grammar.number && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
              {translate("vocabulary_label_number")}:{" "}
              {translationDetails.grammar.number}
            </span>
          )}
          {translationDetails.grammar.gender && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
              {translate("vocabulary_label_gender")}:{" "}
              {translationDetails.grammar.gender}
            </span>
          )}
        </div>
        {showMoreGrammar && (
          <div className="mt-2 space-y-1 text-xs text-slate-600">
            {translationDetails.grammar.mood && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_mood")}:
                </span>{" "}
                {translationDetails.grammar.mood}
              </p>
            )}
            {translationDetails.grammar.person && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_person")}:
                </span>{" "}
                {translationDetails.grammar.person}
              </p>
            )}
            {translationDetails.grammar.case && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_case")}:
                </span>{" "}
                {translationDetails.grammar.case}
              </p>
            )}
            {translationDetails.grammar.syntactic_function && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_syntactic_function")}:
                </span>{" "}
                {translationDetails.grammar.syntactic_function}
              </p>
            )}
            {translationDetails.alternative_meanings && (
              <p>
                <span className="font-semibold">
                  {translate("vocabulary_label_alternatives")}:
                </span>{" "}
                {translationDetails.alternative_meanings}
              </p>
            )}
          </div>
        )}
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={() => setShowMoreGrammar((prev) => !prev)}
            className="px-0 py-0 text-[11px] font-semibold text-slate-600 hover:text-blue-700 focus-visible:outline-none"
          >
            {showMoreGrammar
              ? translate("vocabulary_less_grammar")
              : translate("vocabulary_more_grammar")}
          </button>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-0.5">
          <StageBadge stage={vocabulary.stage} translate={translate} />
          {vocabulary.stage === "LEARNING" && (
            <p className="text-[11px] text-slate-500">
              {translate("vocabulary_progress_stage_learning_interval")}
            </p>
          )}
          {vocabulary.stage === "REVIEWING" && (
            <p className="text-[11px] text-slate-500">
              {translate("vocabulary_progress_stage_reviewing_interval")}
            </p>
          )}
          {vocabulary.stage === "MASTERED" && (
            <p className="text-[11px] text-slate-500">
              {translate("vocabulary_progress_stage_mastered_interval")}
            </p>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <span>{vocabulary.winning_streak}</span>
          <InformationCircleIcon className="h-4 w-4 text-slate-400" title={translate(streakStageKey(vocabulary.winning_streak))} />
        </div>
      </TableCell>
      <TableCell>{lastPracticedCopy}</TableCell>
      <TableCell>{nextReviewCopy}</TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() =>
              markVocabularyAsForgotten.mutateAsync({
                vocabularyId: vocabulary.id,
              })
            }
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:border-rose-200 hover:text-rose-600 transition-colors"
          >
            <HandThumbDownIcon className="h-4 w-4" />
            {translate("vocabulary_progress_actions_forgotten")}
          </button>
          <button
            onClick={() =>
              markVocabularyAsLearned.mutateAsync({
                vocabularyId: vocabulary.id,
              })
            }
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:border-emerald-200 hover:text-emerald-600 transition-colors"
          >
            <HandThumbUpIcon className="h-4 w-4" />
            {translate("vocabulary_progress_actions_learned")}
          </button>
          <button
            onClick={() => onDeleteRequest(vocabulary)}
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs text-rose-600 hover:border-rose-300 hover:bg-rose-50 transition-colors"
          >
            <TrashIcon className="h-4 w-4" />
            {translate("vocabulary_progress_actions_delete")}
          </button>
        </div>
      </TableCell>
    </tr>
  );
}

function VocabularyCardRow({
  vocabulary,
  translate,
  showMoreGrammar,
  setShowMoreGrammar,
  onDeleteRequest,
}: {
  vocabulary: DerivedVocabulary;
  translate: ReturnType<typeof useTranslation>["translate"];
  showMoreGrammar: boolean;
  setShowMoreGrammar: (value: boolean | ((prev: boolean) => boolean)) => void;
  onDeleteRequest: (vocabulary: DerivedVocabulary) => void;
}) {
  const markVocabularyAsLearned = useMarkVocabularyAsLearned();
  const markVocabularyAsForgotten = useMarkVocabularyAsForgotten();
  const translationDetails = useMemo(
    () =>
      normalizeVocabularyTranslationDetails({
        translation: vocabulary.translation,
        translation_details: vocabulary.translation_details,
      }),
    [vocabulary.translation, vocabulary.translation_details],
  );
  const sourceSentence = vocabulary.sentence?.trim() || null;
  const translatedSentence = translationDetails.translated_sentence;
  const primarySentence = sourceSentence;
  const secondarySentence = translatedSentence;

  const nextReviewCopy = vocabulary.nextReviewDate
    ? vocabulary.isDue
      ? translate("progress_due_now")
      : `${translate("progress_due_in")} ${formatRelativeToNow(vocabulary.nextReviewDate)}`
    : translate("progress_never");

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-lg font-semibold text-slate-900">
              {vocabulary.word}
            </p>
            <VocabularyPronunciationButton vocabulary={vocabulary} />
          </div>
          <p className="text-sm text-slate-500">
            {translationDetails.translation_word || vocabulary.translation}
          </p>
          <p className="mt-1 text-left text-xs text-slate-700">
            {primarySentence
              ? highlightVocabularyWordInSentence(primarySentence, vocabulary.word)
              : translate("vocabulary_source_sentence_unavailable")}
          </p>
          {secondarySentence && (
            <p className="mt-1 text-left text-xs text-slate-500">{secondarySentence}</p>
          )}
          <div className="mt-1 flex flex-wrap gap-1 text-[11px]">
            {translationDetails.grammar.part_of_speech && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                {translate("vocabulary_label_pos")}:{" "}
                {translationDetails.grammar.part_of_speech}
              </span>
            )}
            {translationDetails.grammar.tense && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                {translate("vocabulary_label_tense")}:{" "}
                {translationDetails.grammar.tense}
              </span>
            )}
            {translationDetails.grammar.number && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                {translate("vocabulary_label_number")}:{" "}
                {translationDetails.grammar.number}
              </span>
            )}
            {translationDetails.grammar.gender && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                {translate("vocabulary_label_gender")}:{" "}
                {translationDetails.grammar.gender}
              </span>
            )}
          </div>
          {showMoreGrammar && (
            <div className="mt-2 space-y-1 text-xs text-slate-600">
              {translationDetails.grammar.mood && (
                <p>
                  <span className="font-semibold">
                    {translate("vocabulary_label_mood")}:
                  </span>{" "}
                  {translationDetails.grammar.mood}
                </p>
              )}
              {translationDetails.grammar.person && (
                <p>
                  <span className="font-semibold">
                    {translate("vocabulary_label_person")}:
                  </span>{" "}
                  {translationDetails.grammar.person}
                </p>
              )}
              {translationDetails.grammar.case && (
                <p>
                  <span className="font-semibold">
                    {translate("vocabulary_label_case")}:
                  </span>{" "}
                  {translationDetails.grammar.case}
                </p>
              )}
              {translationDetails.grammar.syntactic_function && (
                <p>
                  <span className="font-semibold">
                    {translate("vocabulary_label_syntactic_function")}:
                  </span>{" "}
                  {translationDetails.grammar.syntactic_function}
                </p>
              )}
              {translationDetails.alternative_meanings && (
                <p>
                  <span className="font-semibold">
                    {translate("vocabulary_label_alternatives")}:
                  </span>{" "}
                  {translationDetails.alternative_meanings}
                </p>
              )}
            </div>
          )}
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => setShowMoreGrammar((prev) => !prev)}
              className="px-0 py-0 text-[11px] font-semibold text-slate-600 hover:text-blue-700 focus-visible:outline-none"
            >
              {showMoreGrammar
                ? translate("vocabulary_less_grammar")
                : translate("vocabulary_more_grammar")}
            </button>
          </div>
        </div>
        <StageBadge stage={vocabulary.stage} translate={translate} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 text-sm text-slate-600">
        <div>
          <p className="text-xs uppercase text-slate-400">
            {translate("progress_streak_label")}
          </p>
          <p className="font-semibold">{vocabulary.winning_streak}</p>
        </div>
        <div>
          <p className="text-xs uppercase text-slate-400">
            {translate("progress_next_review")}
          </p>
          <p className="font-semibold">{nextReviewCopy}</p>
        </div>
        <div>
          <p className="text-xs uppercase text-slate-400">
            {translate("progress_last_practiced")}
          </p>
          <p className="font-semibold">
            {vocabulary.last_tested
              ? new Intl.DateTimeFormat(undefined, {
                  month: "short",
                  day: "numeric",
                }).format(new Date(vocabulary.last_tested))
              : translate("progress_never")}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase text-slate-400">
            {translate("vocabulary_progress_table_language")}
          </p>
          <p className="font-semibold">
            {vocabulary.word_language.replaceAll("_", " ")}
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={() =>
            markVocabularyAsForgotten.mutateAsync({
              vocabularyId: vocabulary.id,
            })
          }
          className="flex-1 inline-flex items-center justify-center gap-1 rounded-full border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:border-rose-200 hover:text-rose-600 transition-colors"
        >
          <HandThumbDownIcon className="h-4 w-4" />
          {translate("vocabulary_progress_actions_forgotten")}
        </button>
        <button
          onClick={() =>
            markVocabularyAsLearned.mutateAsync({
              vocabularyId: vocabulary.id,
            })
          }
          className="flex-1 inline-flex items-center justify-center gap-1 rounded-full border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:border-emerald-200 hover:text-emerald-600 transition-colors"
        >
          <HandThumbUpIcon className="h-4 w-4" />
          {translate("vocabulary_progress_actions_learned")}
        </button>
        <button
          onClick={() => onDeleteRequest(vocabulary)}
          className="flex-1 inline-flex items-center justify-center gap-1 rounded-full border border-slate-200 px-3 py-2 text-sm text-rose-600 hover:border-rose-300 hover:bg-rose-50 transition-colors"
        >
          <TrashIcon className="h-4 w-4" />
          {translate("vocabulary_progress_actions_delete")}
        </button>
      </div>
    </div>
  );
}

function TableHeader({ children }: { children: ReactNode }) {
  return (
    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
      {children}
    </th>
  );
}

function TableCell({ children }: { children: ReactNode }) {
  return <td className="px-4 py-3 align-top">{children}</td>;
}

function StageBadge({
  stage,
  translate,
}: {
  stage: Exclude<VocabularyStageWithAll, "ALL">;
  translate: ReturnType<typeof useTranslation>["translate"];
}) {
  const colorMap: Record<Exclude<VocabularyStageWithAll, "ALL">, string> = {
    DUE: "bg-rose-100 text-rose-800",
    NEW: "bg-blue-100 text-blue-800",
    LEARNING: "bg-amber-100 text-amber-800",
    REVIEWING: "bg-indigo-100 text-indigo-800",
    MASTERED: "bg-emerald-100 text-emerald-800",
  };
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full px-3 py-1 text-xs font-semibold text-center ${colorMap[stage]}`}
    >
      {translate(
        STAGE_META.find((meta) => meta.id === stage)?.labelKey ??
          "progress_stage_new",
      )}
    </span>
  );
}

function DeleteVocabularyDialog({
  vocabulary,
  translate,
  isLoading,
  onCancel,
  onConfirm,
}: {
  vocabulary: DerivedVocabulary;
  translate: ReturnType<typeof useTranslation>["translate"];
  isLoading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 px-4 py-8">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-slate-900">
          {translate("vocabulary_progress_delete_title")}
        </h3>
        <p className="mt-2 text-sm text-slate-600">
          {translate("vocabulary_progress_delete_message", {
            word: vocabulary.word,
          })}
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:border-slate-400 hover:text-slate-900 transition-colors disabled:opacity-50"
            onClick={onCancel}
            disabled={isLoading}
          >
            {translate("vocabulary_progress_delete_cancel")}
          </button>
          <button
            type="button"
            className="rounded-full bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rose-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading
              ? translate("vocabulary_progress_delete_pending")
              : translate("vocabulary_progress_delete_confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-sm transition-colors ${
        active
          ? "border-slate-900 bg-slate-900 text-white"
          : "border-slate-200 text-slate-600 hover:border-slate-900 hover:text-slate-900"
      }`}
      type="button"
    >
      {label}
    </button>
  );
}

function LanguageFilterDropdown({
  selectedLanguages,
  onChange,
  options,
  translate,
  disabled = false,
}: {
  selectedLanguages: string[];
  onChange: (languages: string[]) => void;
  options: { value: string; label: string }[];
  translate: ReturnType<typeof useTranslation>["translate"];
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const activeLabel =
    selectedLanguages.length === 0
      ? options[0]?.label ?? ""
      : `${selectedLanguages.length} ${translate(
          "vocabulary_progress_filters_language_selected_suffix",
        )}`;

  return (
    <div className="relative w-full">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setOpen((prev) => !prev)}
        className={`flex w-full items-center justify-between rounded-full border px-4 py-2 text-left text-sm ${
          disabled
            ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
            : "border-slate-200 text-slate-700 hover:border-slate-900"
        }`}
        disabled={disabled}
      >
        <span className="truncate">{activeLabel}</span>
        <svg
          className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m19.5 8.25-7.5 7.5-7.5-7.5"
          />
        </svg>
      </button>
      {open && (
        <div
          ref={menuRef}
          className="absolute z-30 mt-2 w-full rounded-2xl border border-slate-200 bg-white p-3 shadow-lg"
        >
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">
            {translate("spaced_preview_language_picker_hint")}
          </p>
          <div className="max-h-56 overflow-y-auto space-y-1">
            {options.map((option) => (
              <label
                key={option.value}
                className="flex items-center justify-between rounded-lg px-2 py-1 text-sm hover:bg-slate-50"
              >
                <span>{option.label}</span>
                <input
                  type="checkbox"
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  disabled={disabled}
                  checked={
                    selectedLanguages.length === 0 && option.value === "ALL"
                      ? true
                      : selectedLanguages.includes(option.value)
                  }
                  onChange={() => {
                    if (option.value === "ALL") {
                      onChange([]);
                      return;
                    }
                    if (selectedLanguages.includes(option.value)) {
                      onChange(
                        selectedLanguages.filter(
                          (value) => value !== option.value,
                        ),
                      );
                    } else {
                      onChange([...selectedLanguages, option.value]);
                    }
                  }}
                />
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SrsInfoPanel({
  translate,
}: {
  translate: ReturnType<typeof useTranslation>["translate"];
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between text-left"
      >
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            {translate("vocabulary_progress_srs_heading")}
          </p>
          <p className="text-base font-semibold text-slate-900">
            {translate("vocabulary_progress_srs_title")}
          </p>
        </div>
        <div className="flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1 text-xs text-slate-600">
          <InformationCircleIcon className="h-4 w-4" />
          {translate(
            open
              ? "vocabulary_progress_srs_toggle_hide"
              : "vocabulary_progress_srs_toggle_show",
          )}
        </div>
      </button>
      {open && (
        <div className="mt-4 space-y-4 text-sm text-slate-600">
          <p>{translate("vocabulary_progress_srs_intro")}</p>
          <div>
            <p className="font-semibold text-slate-800">
              {translate("vocabulary_progress_srs_actions_heading")}
            </p>
            <ul className="mt-1 list-disc space-y-1 pl-4">
              <li>{translate("vocabulary_progress_srs_action_known")}</li>
              <li>{translate("vocabulary_progress_srs_action_almost")}</li>
              <li>{translate("vocabulary_progress_srs_action_unknown")}</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-slate-800">
              {translate("vocabulary_progress_srs_schedule_heading")}
            </p>
            <dl className="mt-2 grid gap-3 sm:grid-cols-2">
              {SRS_SCHEDULE.map((entry) => (
                <div
                  key={entry.streakLabel}
                  className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2"
                >
                  <dt className="text-xs uppercase tracking-wide text-slate-500">
                    {translate("vocabulary_progress_srs_streak_label", {
                      streak: entry.streakLabel,
                    })}
                  </dt>
                  <dd className="text-sm font-semibold text-slate-800">
                    {translate(entry.translationKey)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
          <p className="text-xs text-slate-500">
            {translate("vocabulary_progress_srs_due_hint")}
          </p>
        </div>
      )}
    </div>
  );
}
